import type { EsProduct } from '~layers/app/types/elastic'
import type { MagentoCart, MagentoCartItem, MagentoGtmOrder } from '~layers/app/types/magento'

// According to https://docs.xsarus.net/nl/datadm/datalayer

export type MagentoCategory = {
  id?: string
  name?: string
}

export type CustomerRegister = {
  firstname: string
  lastname: string
  is_subscribed?: string
  email: string
}

export type DataLayerEcomEvent = {
  coupon?: string
  currency: string
  payment_type?: string
  shipping?: number
  shipping_tier?: string
  tax?: number
  transaction_id?: string
  value?: number
  value_excluded_tax_shipping?: number
  customer?: {
    email?: string
    firstname?: string
    lastname?: string
    newsletter?: boolean
    account?: string
    phone?: string | null
    street?: string
    city?: string
    postcode?: string
    country?: string
  }
  category?: MagentoCategory
}

export type DataLayerItem = {
  affiliation?: string
  coupon?: string
  discount?: number
  item_name: string
  item_id: string
  price: number
  item_size?: string
  item_type?: string
  item_colour?: string
  item_brand?: string
  item_category?: string
  item_category2?: string
  item_category3?: string
  item_category4?: string
  item_category5?: string
  item_category6?: string
  item_category7?: string
  item_category8?: string
  item_category9?: string
  item_list_name?: string
  item_list_id?: string
  item_variant?: string
  index?: number
  item_review?: number
  is_item_in_stock?: number
  location_id?: string
  quantity?: number
  skus?: string[]
  sku?: string
}

export function useDataLayer() {
  const route = useRoute()
  const { email, billingAddress } = useCart()
  const { subscribe } = useNewsletter()

  let proxy = {
    dataLayer: {
      push: (data: Record<string, unknown>) => {
        console.log('Dummy DataLayer push:', data)
      },
    },
  }

  if (route.query.thirdparty !== 'disabled') {
    const { proxy: realProxy } = useScriptGoogleTagManager()
    proxy = realProxy
  }

  const mapCart = (cart: MagentoCart, overrides?: Partial<DataLayerEcomEvent>): DataLayerEcomEvent => {
    const coupon = (cart.applied_coupons || []).map(coupon => coupon.code).join(',')
    const selectedShippingMethod = cart.shipping_addresses?.[0]?.selected_shipping_method
    const shippingTier = selectedShippingMethod ? `${selectedShippingMethod.carrier_title} ${selectedShippingMethod.method_title}` : ''
    const customer = billingAddress.value
      ? {
          email: email.value,
          firstname: billingAddress.value?.firstname,
          lastname: billingAddress.value?.lastname,
          street: billingAddress.value?.street?.[0] + ' ' + billingAddress.value?.street?.[1],
          city: billingAddress.value?.city,
          country: billingAddress.value?.country?.code,
          postcode: billingAddress.value?.postcode,
          phone: billingAddress.value?.telephone,
          newsletter: subscribe.value,
        }
      : undefined
    return {
      currency,
      value: cart.prices.grand_total.value,
      ...(coupon ? { coupon } : {}),
      ...(shippingTier ? { shipping_tier: shippingTier } : {}),
      ...(customer ? { customer } : {}),
      ...overrides,
    }
  }

  const mapCartItem = (item: MagentoCartItem): { product: EsProduct, overrides?: Partial<DataLayerItem> } => ({
    product: item.product.product,
    overrides: {
      quantity: item.quantity,
      item_size: item.configurable_options?.[0]?.value_label || 'one-size',
      sku: item.configured_variant ? item.configured_variant.sku : item.product.sku,
    },
  })

  const formatGa4Item = (item: { product: EsProduct, overrides?: Partial<DataLayerItem> }): DataLayerItem => {
    const { product, overrides } = item
    const result: DataLayerItem = {
      item_name: product?.name,
      item_id: product?.sku,
      price: product.finalPrice || 0,
      item_colour: product.color?.[0]?.label || '',
      item_brand: product.brand?.label || '',
      // is_item_in_stock: product.stockStatus ? 1 : 0,
    }
    let categories = [...(product.categories || [])]
      .sort((a, b) => a.level - b.level)
    if (categories.length > 0) {
      const deepestPath = categories?.[categories.length - 1]?.path
      categories = categories.filter(category => deepestPath?.startsWith(category.path))
      let index = 1
      for (const category of categories) {
        result['item_category' + (index === 1 ? '' : index)] = category.name
        index++
      }
    }
    return { ...result, ...overrides }
  }

  const ecomGa4ItemEvent = (
    event: string,
    ecomEventData: DataLayerEcomEvent,
    items: { product: EsProduct, overrides?: Partial<DataLayerItem> }[],
  ) => {
    proxy?.dataLayer?.push({ ecommerce: null })
    proxy?.dataLayer?.push({
      event,
      ecommerce: {
        ...ecomEventData,
        items: items.map(
          ({ product, overrides }, index) => formatGa4Item({
            product,
            overrides: { index, ...overrides },
          }),
        ),
      },
    })
  }

  const currency = 'EUR'

  const viewItemList = (products: EsProduct[], overrides?: Partial<DataLayerItem>, category?: Partial<MagentoCategory>) => {
    ecomGa4ItemEvent('view_item_list', { currency, category }, products.map(product => ({ product, overrides })))
  }

  const selectItem = (product: EsProduct, overrides?: Partial<DataLayerItem>) => {
    ecomGa4ItemEvent('select_item', { currency }, [{ product, overrides }])
  }

  const viewItem = (product: EsProduct, overrides?: Partial<DataLayerItem>) => {
    ecomGa4ItemEvent(
      'view_item',
      {
        currency,
        // value: '@TODO',
      },
      [{ product, overrides }],
    )
  }

  const addToCart = (cartItems: MagentoCartItem[]) => {
    const items = cartItems.map(mapCartItem)
    const finalPrice = computed(() => {
      return items.reduce((sum, item) => {
        return sum + (item.product.finalPrice * (item.overrides?.quantity || 1))
      }, 0)
    })
    ecomGa4ItemEvent(
      'add_to_cart',
      {
        currency,
        value: finalPrice.value || 0,
      },
      items,
    )
  }

  const addToWishList = (product: EsProduct, overrides?: Partial<DataLayerItem>) => {
    const finalPrice = product.finalPrice * (overrides?.quantity ?? 1)
    ecomGa4ItemEvent(
      'add_to_wishlist',
      {
        currency,
        value: finalPrice || 0,
      },
      [{ product, overrides }],
    )
  }

  const removeFromWishList = (product: EsProduct, overrides?: Partial<DataLayerItem>) => {
    const finalPrice = product.finalPrice * (overrides?.quantity ?? 1)
    ecomGa4ItemEvent(
      'remove_from_wishlist',
      {
        currency,
        value: finalPrice || 0,
      },
      [{ product, overrides }],
    )
  }

  const viewCart = (cart: MagentoCart, overrides?: Partial<DataLayerEcomEvent>) => {
    ecomGa4ItemEvent('view_cart', mapCart(cart, overrides), cart.items.map(mapCartItem))
  }

  const removeFromCart = (cartItem: MagentoCartItem) => {
    const item = mapCartItem(cartItem)
    const finalPrice = item.product?.finalPrice * (item.overrides?.quantity ?? 1)
    ecomGa4ItemEvent(
      'remove_from_cart',
      {
        currency,
        value: finalPrice || 0,
      },
      [item],
    )
  }

  const beginCheckout = (cart: MagentoCart, overrides?: Partial<DataLayerEcomEvent>) => {
    ecomGa4ItemEvent('begin_checkout', mapCart(cart, overrides), cart.items.map(mapCartItem))
  }

  const addShippingInfo = (cart: MagentoCart, overrides?: Partial<DataLayerEcomEvent>) => {
    ecomGa4ItemEvent('add_shipping_info', mapCart(cart, overrides), cart.items.map(mapCartItem))
  }

  const addPaymentInfo = (cart: MagentoCart, overrides?: Partial<DataLayerEcomEvent>) => {
    ecomGa4ItemEvent('add_payment_info', mapCart(cart, overrides), cart.items.map(mapCartItem))
  }

  const purchase = (gtmOrder: MagentoGtmOrder) => {
    ecomGa4ItemEvent(
      'purchase',
      {
        currency,
        value: gtmOrder.purchase.actionField.revenue,
        transaction_id: gtmOrder.purchase.actionField.id,
        tax: gtmOrder.purchase.actionField.tax,
        shipping: gtmOrder.purchase.actionField.shipping,
        ...(gtmOrder.purchase.actionField.coupon ? { coupon: gtmOrder.purchase.actionField.coupon } : {}),
        payment_type: gtmOrder.purchase.actionField.payment_type || undefined,
        shipping_tier: gtmOrder.purchase.actionField.shipping_tier || undefined,
        value_excluded_tax_shipping: gtmOrder.purchase.products.reduce((acc: unknown, product: unknown) => acc + product.price * product.quantity, 0),
        customer: {
          email: gtmOrder.customer.email,
          firstname: gtmOrder.customer.firstname,
          lastname: gtmOrder.customer.lastname,
          street: gtmOrder.customer.street,
          city: gtmOrder.customer.city,
          country: gtmOrder.customer.country,
          postcode: gtmOrder.customer.postcode,
          phone: gtmOrder.customer.phone,
          newsletter: !!gtmOrder.customer.newsletter,
        },
        // items: '@TODO'
      },
      gtmOrder.purchase.products.map((product, index): { product: EsProduct, overrides?: Partial<DataLayerItem> } => ({
        product: product.product,
        overrides: {
          ...(product.coupon ? { coupon: product.coupon } : {}),
          index,
          ...(product.variant ? { item_variant: product.variant } : {}),
          quantity: product.quantity,
        },
      })),
    )
  }

  const pageView = (pageType: string, pageTitle?: string) => {
    watch(
      () => route.href,
      (href) => {
        proxy?.dataLayer?.push({
          event: 'pageView',
          pageType,
          pageUrl: href,
          pageTitle: pageTitle || pageType,
        })
      },
      { immediate: true })
  }

  const cookiePreference = () => {
    proxy?.dataLayer?.push({ event: 'cookiePreference' })
  }

  const requestQuote = (products: EsProduct[], customer: CustomerRegister) => {
    proxy?.dataLayer?.push({
      event: 'request_quote',
      ecommerce: {
        currency: '@TODO',
        value: '@TODO',
        quote_id: '@TODO',
        items: products.map((product, index): DataLayerItem => ({
          item_name: product.name,
          item_id: product.sku,
          price: product.finalPrice || 0,
          index,
          quantity: 1,
        })),
        customer: {
          customer_id: '@TODO',
          firstname: customer.firstname,
          lastname: customer.lastname,
          email: customer.email,
        },
      },
    })
  }

  const completeRegistration = (customer: CustomerRegister) => {
    proxy?.dataLayer?.push({
      event: 'complete_registration',
      customer: {
        firstname: customer.firstname,
        lastname: customer.lastname,
        birthdate: '@TODO',
        gender: '@TODO',
        language: '@TODO',
        newsletter: customer.is_subscribed ? 'yes' : 'no',
        email: customer.email,
      },
    })
  }

  const newsletterSubscription = (email: string) => {
    proxy?.dataLayer?.push({ event: 'email_optin', customer: { email: email } })
  }

  const accountLogin = (email: string) => {
    proxy?.dataLayer?.push({ event: 'account_login', customer: { email: email } })
  }

  const stockRequest = (email: string, product: EsProduct, overrides?: Partial<DataLayerItem>, is_subscribed: boolean = false) => {
    proxy?.dataLayer?.push({
      event: 'stock_request',
      ecommerce: {
        email: email,
        newsletter: is_subscribed ? 'yes' : 'no',
        product: formatGa4Item({ product, overrides }),
      },
    })
  }

  return {
    accountLogin,
    addPaymentInfo,
    addShippingInfo,
    addToCart,
    addToWishList,
    removeFromWishList,
    beginCheckout,
    completeRegistration,
    cookiePreference,
    newsletterSubscription,
    pageView,
    purchase,
    removeFromCart,
    requestQuote,
    selectItem,
    stockRequest,
    viewCart,
    viewItem,
    viewItemList,
  }
}
